import { ReactNode, useMemo, useEffect, useState } from 'react'
import { ThemeProvider as MuiThemeProvider, CssBaseline, createTheme } from '@mui/material'
import { useUIStore } from '@store/uiStore'
import { themeRegistry } from '@themes/registry'
import { useMyThemes } from '@hooks/useThemes'
import { buildThemeConfig } from '@themes/buildTheme'

interface ThemeProviderProps {
  children: ReactNode
}

function useSystemDarkMode() {
  const [prefersDark, setPrefersDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  )

  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setPrefersDark(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return prefersDark
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { themeName, themeMode } = useUIStore()
  const systemPrefersDark = useSystemDarkMode()
  const { data: serverThemes } = useMyThemes()

  // Register server themes into the registry when they arrive
  useEffect(() => {
    if (!serverThemes) return

    // Clear previous server themes and re-register from fresh response
    themeRegistry.clearServerThemes()

    for (const dto of serverThemes) {
      const config = buildThemeConfig(dto)
      themeRegistry.register(config)
    }

    // If the user's selected theme was revoked, fall back to adventure
    if (!themeRegistry.getTheme(themeName)) {
      useUIStore.getState().setTheme('adventure')
    }
  }, [serverThemes, themeName])

  const theme = useMemo(() => {
    // Get theme config from registry, fallback to adventure
    const themeConfig = themeRegistry.getTheme(themeName) || themeRegistry.getTheme('adventure')!

    // Resolve effective mode: auto defers to OS preference
    const effectiveMode =
      themeMode === 'auto' ? (systemPrefersDark ? 'dark' : 'light') : themeMode

    // Select light or dark mode
    const themeOptions = effectiveMode === 'dark' ? themeConfig.dark : themeConfig.light

    // Create MUI theme
    return createTheme(themeOptions)
  }, [themeName, themeMode, systemPrefersDark, serverThemes])

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  )
}
